/**
 * Global authentication context.
 *
 * Holds the current user profile and exposes login/register/logout actions.
 * On mount, if a persisted access token exists, it attempts to hydrate the
 * current user via `GET /auth/me`.
 */

import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import authService, { type RegisterPayload } from "@/services/authService";
import { tokenStorage } from "@/services/api";
import type { ModuleCode, User } from "@/types";
import { trackEvent } from "@/services/analyticsService";

export interface AuthContextValue {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  hasRole: (role: string) => boolean;
  hasModule: (moduleCode: ModuleCode) => boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (payload: RegisterPayload) => Promise<User>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const refreshUser = useCallback(async () => {
    if (!tokenStorage.getAccessToken()) {
      setUser(null);
      return;
    }
    try {
      const profile = await authService.me();
      setUser(profile);
    } catch {
      setUser(null);
      tokenStorage.clear();
    }
  }, []);

  useEffect(() => {
    refreshUser().finally(() => setIsLoading(false));
  }, [refreshUser]);

  // Proactively renew the access token every 15 minutes while the app is open
  // and the user is authenticated (access tokens default to a 30-minute
  // lifetime). This keeps long-lived sessions alive without waiting for a
  // 401 to trigger the reactive refresh in the Axios interceptor.
  useEffect(() => {
    if (!tokenStorage.getAccessToken()) return;

    const intervalId = window.setInterval(() => {
      if (tokenStorage.getRefreshToken()) {
        authService.refresh().catch(() => {
          tokenStorage.clear();
          setUser(null);
        });
      }
    }, 15 * 60 * 1000);

    return () => window.clearInterval(intervalId);
  }, [user]);

  const login = useCallback(async (email: string, password: string) => {
    try {
      const response = await authService.login(email, password);
      setUser(response.user);
      trackEvent("login_success", { eventCategory: "AUTH" });
    } catch (err) {
      trackEvent("login_failed", { eventCategory: "AUTH" });
      throw err;
    }
  }, []);

  const register = useCallback(async (payload: RegisterPayload) => {
    const user = await authService.register(payload);
    trackEvent("signup_completed", { eventCategory: "AUTH" });
    return user;
  }, []);

  const logout = useCallback(async () => {
    trackEvent("logout", { eventCategory: "AUTH" });
    await authService.logout();
    setUser(null);
  }, []);

  const hasRole = useCallback(
    (role: string) => Boolean(user?.roles.some((r) => r.name.toUpperCase() === role.toUpperCase())),
    [user]
  );

  // User Onboarding + Module Access (Pilot): superuser/ADMIN bypass mirrors
  // the backend's `require_module` dependency exactly, so an admin's nav
  // never hides something they're actually allowed to open.
  const hasModule = useCallback(
    (moduleCode: ModuleCode) =>
      Boolean(user?.is_superuser || hasRole("ADMIN") || user?.modules.includes(moduleCode)),
    [user, hasRole]
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isAuthenticated: Boolean(user),
      isLoading,
      hasRole,
      hasModule,
      login,
      register,
      logout,
      refreshUser,
    }),
    [user, isLoading, hasRole, hasModule, login, register, logout, refreshUser]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
